import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Input,
  Select
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined,SearchOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ModuleModal from './ModuleModel';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Modules {
  module_id: number;
  module_name: string;
  module_url: string;
  module_parent: number;
  module_type: string;
  module_status: number;
  module_order: number;
  module_icon: string;
  module_show_on_menu?: string;
}

const Module: React.FC = () => {
  const [modules, setModules] = useState<Modules[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Modules | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


    const [keywordSearch, setkeywordSearch] = useState('');


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchModule = async () => {
    try {
      const data = await apiRequest<Modules[]>({
        method: 'get',
        url: 'modules?keyword=' + keywordSearch,
      });
      setModules(data);
    } catch (error) {
      showError('Failed to fetch modules');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchModule();
  }, []);

  const handleEditClick = (record: Modules) => {
    setIsEditMode(true);
    setInitialValues({
      module_id: record.module_id,
      module_name: record.module_name,
      module_status: record.module_status,
      module_url: record.module_url,
      module_parent: record.module_parent,
      module_type: record.module_type,
      module_order: record.module_order,
      module_icon: record.module_icon
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchModule();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (module_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-module/${module_id}`,
      });
      showSuccess('Module deleted');
      fetchModule();
    } catch {
      showError('Failed to delete Module');
    }
  };

  const handleStatusToggle = async (moduletId: number) => {
    setModules((prev) =>
      prev.map((item) =>
        item.module_id === moduletId
          ? { ...item, module_status: item.module_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-module/${moduletId}`,
      });
      showSuccess('Module status updated');
    } catch (error) {
      showError('Failed to update status');
      setModules((prev) =>
        prev.map((item) =>
          item.module_id === moduletId
            ? { ...item, module_status: item.module_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleMenuVisibilityToggle = async (moduleId: number) => {
    setModules((prev) =>
      prev.map((item) =>
        item.module_id === moduleId
          ? { ...item, module_show_on_menu: item.module_show_on_menu === 'Y' ? 'N' : 'Y' }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-menu-visibility-module/${moduleId}`,
      });
      showSuccess('Menu visibility updated successfully');
    } catch (error) {
      showError('Failed to update menu visibility');
      // Rollback on failure
      setModules((prev) =>
        prev.map((item) =>
          item.module_id === moduleId
            ? { ...item, module_show_on_menu: item.module_show_on_menu === 'Y' ? 'N' : 'Y' }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Modules>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };


  const parentNameMap = new Map<number, string>(
    modules.map((m) => [m.module_id, m.module_name])
  );



  const columns: TableColumnsType<Modules> = [
    {
      title: 'Module Name',
      dataIndex: 'module_name',
      key: 'module_name',
      filters: Array.from(new Set(modules.map((s) => s.module_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.module_name || null,
      onFilter: (value, record) => record.module_name.includes(value as string),
      sorter: (a, b) => a.module_name.localeCompare(b.module_name),
      sortOrder: sortedInfo.columnKey === 'module_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.module_name}</div>,
    },
    {
      title: 'Module Parent Name',
      dataIndex: 'module_parent', // this is the ID
      key: 'module_parent',

      // Create unique filters from parent names (plus "Root")
      filters: Array.from(new Set(
        modules.map(m => parentNameMap.get(m.module_parent) || 'Root')
      )).map(name => ({
        text: name,
        value: name,
      })),

      filterSearch: true,
      filterMode: 'tree',

      filteredValue: filteredInfo.module_parent || null,

      onFilter: (value, record) => {
        const parentName = parentNameMap.get(record.module_parent) || 'Root';
        return parentName === value;
      },

      sorter: (a, b) => {
        const nameA = parentNameMap.get(a.module_parent) || 'Root';
        const nameB = parentNameMap.get(b.module_parent) || 'Root';
        return nameA.localeCompare(nameB);
      },

      sortOrder: sortedInfo.columnKey === 'module_parent' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,

      render: (_, record) => (
        <div>{parentNameMap.get(record.module_parent) || 'Root'}</div>
      ),
    },
    {
      title: 'Module Order',
      dataIndex: 'module_order',
      key: 'module_order',
      render: (_, record) => <div>{record.module_order}</div>,
    },
    {
      title: 'Show On Menu',
      dataIndex: 'module_show_on_menu',
      key: 'module_show_on_menu',
      width: 140,
      render: (_, record) => (
        <Switch
          checkedChildren="Show"
          unCheckedChildren="Hide"
          checked={record.module_show_on_menu === 'Y'}
          disabled={currentModule?.right_edit !== 'Y'}
          onChange={() => handleMenuVisibilityToggle(record.module_id)}
        />
      ),
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Modules) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.module_status}
                onChange={() => handleStatusToggle(record.module_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this module?"
              onConfirm={() => handleDelete(record.module_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  const handleKeywordSearch = (value: string) => {
    setkeywordSearch(value);
    fetchModule();  // fetch filtered ledgers on the server
  };

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Modules</Breadcrumb.Item>
        </Breadcrumb>
        <Flex gap="small" align="center">
          <Input.Search
            placeholder="Search by keyword"
            allowClear
            onSearch={handleKeywordSearch}
            style={{ width: 200, marginRight: 16, padding: 10 }}
            value={keywordSearch}
            onChange={e => setkeywordSearch(e.target.value)}
            enterButton={<SearchOutlined />}  // <-- search icon button
            size="middle"
            className="no-border-radius-search left-rounded"
          />
        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Module
            </Button>
          </Flex>
        )}
        </Flex>
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Modules>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="module_id"
          dataSource={modules.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No module found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, modules.length)} of{' '}
                {modules.length}
              </span>
              <Pagination
                {...pagination}
                total={modules.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ModuleModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        modules={modules}
        initialValues={initialValues}
      />
    </>
  );
};

export default Module;